/**
 * Navigation Auth Configuration
 *
 * Centralized configuration for SSO vs standalone auth behavior in the navigation.
 * Derived from IsConductorClient (CLIENT_TYPE === 'conductor').
 *
 * SSO (Conductor): JWT, app.conductor.com, ConductorCookieCurrentSessionAdapter
 * Standalone: PHP session, magic link/credentials/Google, NetteUserCurrentSessionAdapter
 *
 * Use this config instead of scattered IsConductorClient checks in navigation components.
 */

import {
	CONDUCTOR_APP_URL,
	IsConductorClient,
} from '~/config';



export type NavigationAuthType = 'sso' | 'standalone';

export type NavigationAuthConfig = {
	/** Whether the user is on SSO (Conductor) or standalone auth */
	authType: NavigationAuthType;
	/** Show Conductor link in navigation (SSO only) */
	showConductorLink: boolean;
};

const MARKETING_CONTENT_URL = 'https://www.conductor.com/platform/content-creation-optimization/';
const MARKETING_PERFORMANCE_URL = 'https://www.conductor.com/platform/intelligence/';

/**
 * Get Content nav URL for new navigation.
 * SSO: Conductor smart link (redirects to user's most recent account/context).
 * Standalone: marketing landing page.
 */
export function getContentNavUrl(): string {
	if (IsConductorClient) {
		return `${CONDUCTOR_APP_URL}/u/content`;
	}

	return MARKETING_CONTENT_URL;
}

/**
 * Get Performance nav URL for new navigation.
 * SSO: Conductor smart link (redirects to user's most recent account/context).
 * Standalone: marketing landing page.
 */
export function getPerformanceNavUrl(): string {
	if (IsConductorClient) {
		return `${CONDUCTOR_APP_URL}/u/performance`;
	}

	return MARKETING_PERFORMANCE_URL;
}

/**
 * Conductor app unified home (same for SSO / standalone).
 * Used for Home in new navigation and the sidebar logo link.
 */
export function getConductorAppHomeUrl(): string {
	return `${CONDUCTOR_APP_URL}/u/home`;
}

/**
 * Get navigation auth configuration based on IsConductorClient
 *
 * @returns NavigationAuthConfig object with auth type and feature flags
 */
export function getNavigationAuthConfig(): NavigationAuthConfig {
	return IsConductorClient
		? {
			authType: 'sso',
			showConductorLink: true,
		}
		: {
			authType: 'standalone',
			showConductorLink: false,
		};
}
